import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Popup from './Popup';
import io from 'socket.io-client';
import Message from './Message';
import ChatModeSelectionModal from './ChatModeSelectionModal';
import '../styles/ChatBox.css';
import { jsPDF } from "jspdf"; // For PDF generation
import {  MdOutlineFileDownload } from "react-icons/md";
import { RiChatNewLine } from "react-icons/ri";
import {FiHeart, FiMessageSquare} from "react-icons/fi"
import axios from 'axios'
import loadingGif from '../assets/Loving-Is-Loading-gif.gif'
import sendIcon from '../assets/send-icon.svg'
import { HiArrowSmUp } from "react-icons/hi";
import { FaBars } from 'react-icons/fa'; // Hamburger icon for mobile
import { IoChatbubblesOutline } from "react-icons/io5";
import { CiLogout } from "react-icons/ci";

const SOCKET_SERVER_URL = process.env.REACT_APP_BACKEND_URL;
const API_SERVER_URL = `${SOCKET_SERVER_URL}/api/chats`;

const ChatBox = ({ userId,showSidebar,setShowSidebar, isModalOpen, setIsModalOpen, isAuthenticated, handleLogout }) => {
    
    const [socket, setSocket] = useState(null);
    const [chats, setChats] = useState([]);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [chatNameUpdated, setChatNameUpdated] = useState(false);
    const [chatMode, setChatMode] = useState('answerChat'); // New state for selected chat mode
    // const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false); 
    const { chatId: urlChatId } = useParams();    
    const [loadingChatId, setLoadingChatId] = useState(null);
    const [pendingMessage, setPendingMessage] = useState(null); // Store message until selectedChatId updates
    const [newChatIds, setNewChatIds] = useState(new Set());
    const messageRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [isNewChat, setIsNewChat] = useState(false);
    const [pendingAnswerChat, setPendingAnswerChat] = useState(null);
    const selectedChatIdRef = useRef(selectedChatId);
    const [isFetchingHistory, setIsFetchingHistory] = useState(false);
    const navigate = useNavigate()
    const location = useLocation()
    const abortControllerRef = useRef(null);


    const currentChat = chats.find(chat => chat.chatId === selectedChatId);
const derivedChatMode = currentChat?.mode || 'standard';


    const handlePopupClose = async () => {
        setIsPopupOpen(false);
        // If a pending answer chat already exists, just use it without creating a new one.
        if (pendingAnswerChat) {
          // Optionally, navigate to that chat and fetch history.
          navigate(`/chat/${pendingAnswerChat}`, { replace: true });
          await fetchChatHistory(pendingAnswerChat);
        }
      };

      useEffect(() => {
        if (selectedChatId && chats.length > 0) {
          const selectedChat = chats.find(chat => chat.chatId === selectedChatId);
          if (selectedChat?.mode) {
            setChatMode(selectedChat.mode);
          }
        }
      }, [selectedChatId, chats]);
      
      const handlePopupSubmit = async (formData) => {
        if (!formData?.question1) return;
        try {
          // Use pendingAnswerChat if already created, otherwise initialize a new answerChat.
          let chatIdToUse = pendingAnswerChat;
          if (!chatIdToUse) {
            // You can either pass formData or simply 'answerChat' to force answer mode.
            chatIdToUse = await initializeChat('answerChat');
            // Alternatively, if formData is required to initialize:
            // chatIdToUse = await initializeChat(formData);
          }
          if (chatIdToUse) {
            // Immediately update the selected chat state & ref
            setSelectedChatId(chatIdToUse);
            selectedChatIdRef.current = chatIdToUse;
            localStorage.setItem('selectedChatId', chatIdToUse);
      
            // Close the popup and navigate to the new chat.
            setIsPopupOpen(false);
            navigate(`/chat/${chatIdToUse}`, { replace: true });
      
            // (Optional) Fetch the history for the new chat if needed.
            await fetchChatHistory(chatIdToUse);
      
            // Combine the form fields into one message.
            const combinedMessage = [formData.question1, formData.question2, formData.question3]
              .filter(Boolean)
              .join(', ');
      
            // Send the combined message to the newly created answer mode chat.
            await sendMessage(chatIdToUse, combinedMessage);
      
            // Clear pending answer chat
            setPendingAnswerChat(null);
          }
        } catch (error) {
          console.error('Error creating answer chat:', error);
        }
      };
      
  
useEffect(() => {
        if (pendingMessage && pendingMessage.chatId) {
          sendMessage(pendingMessage.chatId, pendingMessage.message);
          setPendingMessage(null);
        }
      }, [pendingMessage]);
      
      useEffect(() => {
        if (selectedChatId && pendingAnswerChat === selectedChatId) {
          // Scroll to bottom when answer chat is ready
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }, [selectedChatId, pendingAnswerChat]);

      useEffect(() => {
        selectedChatIdRef.current = selectedChatId;
      }, [selectedChatId]);

      const handleChatModeSelect = async (mode) => {
        setChatMode(mode);
        setIsModalOpen(false);
        setSelectedChatId(null);
        setMessages([]); // Clear out old messages
        localStorage.removeItem('selectedChatId');
        setShowSidebar(false);
      
        if (mode === 'answerChat') {
          try {
            const chatId = await initializeChat('answerChat');
            if (chatId) {
              setPendingAnswerChat(chatId);
              setIsPopupOpen(true);
              navigate(`/chat/${chatId}`);
            }
          } catch (error) {
            console.error('Error creating answer chat:', error);
          }
        } else {
          try {
            const chatId = await initializeChat('standard');
            if (chatId) {
              setSelectedChatId(chatId);
              localStorage.setItem('selectedChatId', chatId);
              navigate(`/chat/${chatId}`, { replace: true });
              await fetchChatHistory(chatId);
              setMessages((prevMessages) => {
                if (prevMessages.length === 0) {
                  return [{ content: 'Hey Love, How can I help you today?' }];
                }
                return prevMessages;
              });
            }
          } catch (error) {
            console.error('Error creating standard chat:', error);
          }
        }
      };
      

  useEffect(() => {
    const newSocket = io(SOCKET_SERVER_URL, { transports: ['websocket'] });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    // Cleanup on unmount
    return () => {
      newSocket.close();
    };
  }, [SOCKET_SERVER_URL]);


  const mergeUniqueMessages = (existing, fetched) => {
    // Use JSON.stringify to compare role+content (or use a better unique id if available)
    const existingKeys = new Set(existing.map(m => `${m.role}-${m.content}`));
    const merged = [...existing];
    fetched.forEach(msg => {
      const key = `${msg.role}-${msg.content}`;
      if (!existingKeys.has(key)) {
        merged.push(msg);
      }
    });
    return merged;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedChatId) return;
    
      // Abort any previous fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
    
      try {
        setIsFetchingHistory(true);
        const currentChat = chats.find((c) => c.chatId === selectedChatId);
        const currentMode = currentChat?.mode || chatMode;
    
        // Immediately clear messages if switching to non-answerChat mode
        if (currentMode !== 'answerChat') {
          setMessages([]);
        }
    
        const response = await fetch(
          `${API_SERVER_URL}/getChatHistory/${selectedChatId}`,
          { signal: abortControllerRef.current.signal }
        );
    
        if (!response.ok) throw new Error('Failed to fetch history');
        
        const data = await response.json();
        const chatMessages = Array.isArray(data?.messages?.messages)
          ? data.messages.messages
          : [];
    
        // Handle different modes
        if (currentMode === 'answerChat') {
          // Merge with existing messages but remove duplicates
          setMessages((prev) => {
            const existingKeys = new Set(prev.map((m) => `${m.role}-${m.content}`));
            const newMessages = chatMessages.filter(
              (msg) => !existingKeys.has(`${msg.role}-${msg.content}`)
            );
            return [...prev, ...newMessages];
          });
        } else {
          // For standard chats, show welcome message if empty
          if (chatMessages.length === 0) {
            setMessages([{ 
              content: 'Hey Love, How can I help you today?', 
              isWelcome: true 
            }]);
          } else {
            setMessages(chatMessages);
          }
        }
    
        // Scroll to bottom after messages update
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
    
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching chat history:', error);
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: 'Failed to load chat history. Please try refreshing.',
              isError: true
            }
          ]);
        }
      } finally {
        setIsFetchingHistory(false);
        setIsNewChat(false);
      }
    };
    
    fetchData();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedChatId, chatMode]);
  
  
    
  useEffect(() => {
    if (!userId || !socket) return;
  
    const fetchChats = async () => {
      try {
        const response = await fetch(`${API_SERVER_URL}/getChats?userEmail=${userId}`);
        const data = await response.json();
        // Server-provided chat list:
        const chatList = Array.isArray(data?.chats) ? data.chats : [];
  
        // Instead of replacing local chats with an empty array,
        // merge them if chatList is empty.
        setChats((prevChats) => {
          if (chatList.length === 0) {
            return prevChats; // Preserve already-known chats
          }
          const updatedChats = [...chatList, ...prevChats];
          return [
            ...new Map(updatedChats.map((chat) => [chat.chatId, chat])).values()
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        });
  
        // Chat selection logic:
        if (urlChatId) {
          // (1) Use chatId from URL.
          setSelectedChatId(urlChatId);
          localStorage.setItem('selectedChatId', urlChatId);
          fetchChatHistory(urlChatId);
        } else {
          const storedChatId = localStorage.getItem('selectedChatId');
          if (storedChatId) {
            // (2) Use chatId from localStorage.
            setSelectedChatId(storedChatId);
            navigate(`/chat/${storedChatId}`, { replace: true });
            fetchChatHistory(storedChatId);
          } else if (
            chatList.length === 0 &&
            !selectedChatId &&
            !pendingAnswerChat
          ) {
            // (3) No chats exist and none is pending/selected.
            if (chatMode === 'answerChat') {
              const newChatId = await initializeChat('answerChat');
              if (newChatId) {
                setPendingAnswerChat(newChatId);
                setSelectedChatId(newChatId);
                localStorage.setItem('selectedChatId', newChatId);
                navigate(`/chat/${newChatId}`, { replace: true });
                setIsPopupOpen(true);
              }
            } else {
              // For standard mode, create a default chat.
              const newChatId = await initializeChat('standard');
              if (newChatId) {
                setSelectedChatId(newChatId);
                localStorage.setItem('selectedChatId', newChatId);
                navigate(`/chat/${newChatId}`, { replace: true });
                await fetchChatHistory(newChatId);
              }
            }
          } else if (chatList.length > 0) {
            // (4) If chats exist, select the latest one.
            const sortedChats = chatList.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            const latestChat = sortedChats[0];
            if (latestChat) {
              setSelectedChatId(latestChat.chatId);
              localStorage.setItem('selectedChatId', latestChat.chatId);
              navigate(`/chat/${latestChat.chatId}`, { replace: true });
              fetchChatHistory(latestChat.chatId);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching chats:', error);
      }
    };
  
    fetchChats();
  }, [userId, urlChatId, navigate, chatMode, socket, API_SERVER_URL, pendingAnswerChat]);

  const fetchChatHistory = async (chatId, options = {}) => {
    const { forceRefresh = false } = options;
    
    if (!forceRefresh && newChatIds.has(chatId)) {
      setNewChatIds(prev => new Set([...prev].filter(id => id !== chatId)));
      return [];
    }

    try {
      const response = await fetch(`${API_SERVER_URL}/getChatHistory/${chatId}`);
      const data = await response.json();
      const chatMessages = Array.isArray(data?.messages?.messages)
        ? data.messages.messages
        : [];

      const currentChat = chats.find((c) => c.chatId === chatId);
      const mode = currentChat?.mode || chatMode;

      if (selectedChatIdRef.current === chatId) {
        if (mode === 'answerChat') {
          setMessages(prev => mergeUniqueMessages(prev, chatMessages));
        } else {
          setMessages(chatMessages.length > 0 ? chatMessages : [
            { content: 'Hey Love, How can I help you today?', isWelcome: true }
          ]);
        }
      }

      return chatMessages;
    } catch (error) {
      if (selectedChatIdRef.current === chatId) {
        setMessages([{
          content: 'Failed to load chat history. Please try refreshing.',
          isError: true
        }]);
      }
      return [];
    }
  };

  

// Handle new chat creation
const initializeChat = async (formDataOrMode = 'standard') => {
  if (isLoading || !socket) return null;
  setIsLoading(true);

  const chatName =
  typeof formDataOrMode === 'object' && formDataOrMode?.question1
    ? formDataOrMode.question1.slice(0, 20)
    : chats.length === 0 && formDataOrMode === 'answerChat'
      ? 'Welcome Chat'
      : `Chat ${chats.length + 1}`;

// Determine the mode: if formDataOrMode is a string, use that; otherwise, use 'answerChat'
const mode = typeof formDataOrMode === 'string' ? formDataOrMode : 'answerChat';

  return new Promise((resolve, reject) => {
    socket.emit('initChat', {
      isGuest: false,
      userEmail: userId,
      name: chatName,
      mode,
    }, (response) => {
      setIsLoading(false);
      if (response?.chatId) {
        const newChat = {
          chatId: response.chatId,
          name: chatName,
          createdAt: new Date().toISOString(),
          mode,
        };
        setChats(prev => [newChat, ...prev]);
        setNewChatIds(prev => new Set([...prev, response.chatId])); 
        setIsNewChat(true);
        
        if (mode === 'answerChat') {
          setSelectedChatId(response.chatId);
          localStorage.setItem('selectedChatId', response.chatId);
        }
        
        resolve(response.chatId);
      } else {
        reject(response?.error || 'Chat initialization failed');
      }
    });
  });
};
  const sendMessage = async (chatId, message) => {
    if (!message || !chatId || !socket) return;
    const trimmedMessage = message.trim();
    const userMessage = {
      role: 'user',
      content: trimmedMessage,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
  
    setLoadingChatId(chatId);
    const currentChat = chats.find((chat) => chat.chatId === chatId);
    const currentMode = currentChat ? currentChat.mode : chatMode;
    // Use different socket events based on mode.
    const socketEvent = currentMode === 'answerChat' ? 'answerChatMode' : 'sendMessage';
  
    return new Promise((resolve) => {
      socket.emit(
        socketEvent,
        { chatId, message: trimmedMessage },
        async (response) => {
          setLoadingChatId(null);

          if (selectedChatIdRef.current !== chatId) {
            // If the user switched chats, ignore this response.
            resolve();
            return;
          }

          if (response?.error) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content:
                  'Oops, it seems the AI went on a coffee break. Please try again.',
              },
            ]);
          } else {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: response.message,
                createdAt: new Date().toISOString(),
                liked: false  
              },
            ]);
            // If the chat name is still generic, update it based on the first message.
            const chat = chats.find((chat) => chat.chatId === chatId);
            if (chat && chat.name.startsWith('Chat')) {
              const newChatName = trimmedMessage.slice(0, 20);
              await updateChatName(chatId, newChatName);
            }
          }
          resolve();
        }
      );
    });
  };
// Function to update the chat name via the API
const updateChatName = async (chatId, newName) => {
    try {
        const response = await axios.put(`${API_SERVER_URL}/${chatId}/name`, { newName });

        if (response.status === 200) {
            console.log('Chat name updated successfully:', response.data);
            // Update the chat name in the state after successful API call
            setChats((prevChats) =>
                prevChats.map((chat) =>
                    chat.chatId === chatId ? { ...chat, name: newName } : chat
                )
            );
            setChatNameUpdated(true); // Set the flag that chat name has been updated
        } else {
            console.error('Failed to update chat name:', response.status, response.data);
            throw new Error('Failed to update chat name');
        }
    } catch (error) {
        console.error('Error in API call to update chat name:', error.message);
        throw error; // Rethrow the error to be handled by the calling function
    }
};

// useEffect to perform an action when the chat name is updated
useEffect(() => {
    if (chatNameUpdated) {
        // Action after chat name is updated, e.g., logging
        console.log('Chat name was updated successfully!');
        setChatNameUpdated(false); // Reset the flag after handling
    }
}, [chatNameUpdated]); 

const handleChatboxSubmit = async (e) => {
    e.preventDefault();
    setInput('');

    if (!input.trim() || loadingChatId) return;
    try {
      let targetChatId = selectedChatId;
      if (!targetChatId) {
        // If no chat exists, initialize one using the current mode.
        targetChatId = await initializeChat(chatMode);
      }
      const formattedInput = input.replace(/\n/g, "<br/>"); 
      await sendMessage(targetChatId, formattedInput);
      setInput('');
    } catch (error) {
      console.error('Message sending failed:', error);
    }
  };

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedChatId) return;
      
      // Abort any previous fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
  
      try {
        setIsFetchingHistory(true);
        const currentChat = chats.find(c => c.chatId === selectedChatId);
  
        // Only clear messages if NOT a new answer chat
        if (!(currentChat?.mode === 'answerChat' && isNewChat)) {
          setMessages([]);
        }
        
        const response = await fetch(
          `${API_SERVER_URL}/getChatHistory/${selectedChatId}`,
          { signal: abortControllerRef.current.signal }
        );
        
        const data = await response.json();
        const chatMessages = Array.isArray(data?.messages?.messages)
          ? data.messages.messages
          : [];
  
        // For standard chats with no history, show welcome message
        if (currentChat?.mode !== 'answerChat' && chatMessages.length === 0) {
          setMessages([{ content: 'Hey Love, How can I help you today?', isWelcome: true }]);
        } else if (currentChat?.mode === 'answerChat' && isNewChat) {
          // In answer mode for new chats:
          // If the server returns any messages, merge them.
          // Otherwise, preserve what is already in state.
          if (chatMessages.length > 0) {
            setMessages(prev => [...prev, ...chatMessages]);
          }
          // Else: do nothing so that the user’s message stays visible.
        } else {
          // For all other cases, update messages with the fetched history.
          setMessages(chatMessages);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching chat history:', error);
        }
      } finally {
        setIsFetchingHistory(false);
        setIsNewChat(false); // reset the flag once fetch is complete
      }
    };
  
    fetchData();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedChatId]);
  
  const handleSelectChat = async (chatId) => {
    if (selectedChatId === chatId || isLoading) return;

    setInput('');
    setIsFetchingHistory(true);
    
    const selectedChat = chats.find(chat => chat.chatId === chatId);
    const isAnswerMode = selectedChat?.mode === 'answerChat';

    setSelectedChatId(chatId);
    localStorage.setItem('selectedChatId', chatId);
    setShowSidebar(false);
    setChatMode(selectedChat?.mode || chatMode);

    if (!isAnswerMode) {
      setMessages([{ content: 'Loading conversation...', isTemp: true }]);
    }

    navigate(`/chat/${chatId}`);

    try {
      await fetchChatHistory(chatId, { forceRefresh: true });
    } catch (error) {
      setMessages([{
        content: 'Failed to load chat. Please try again.',
        isError: true
      }]);
    } finally {
      setIsFetchingHistory(false);
    }
  };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

  useEffect(() => {
      const textarea = document.querySelector(".dynamic-textarea");
      if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
      }
  }, [input]);


  const formatDateTime = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
      });
  };

  const ChatModeIcon = ({ mode }) => {
    if (mode === 'standard') {
      return <FiHeart className="chat-mode-icon" size={16} />;
    }
    if (mode === 'answerChat') {
      return <FiMessageSquare className="chat-mode-icon-answer" size={16} />;
    }
    return null;
  };
  const handleKeyPress = (e) => {
    // Check if the Enter key was pressed (keyCode 13)
    if (e.key === 'Enter' && !e.shiftKey) {
        // Trigger form submission
        e.preventDefault(); // Prevents a new line
        handleChatboxSubmit(e);
    }
};

const handleModalClose = () => {
    setIsModalOpen(false);
};
const updateMessageLike = async (messageIndex, newLikedStatus) => {
  const isWelcomeMessage = messages[messageIndex]?.isWelcome;
  if (isWelcomeMessage) return;

  // Check if FIRST message is welcome message
  const hasWelcomeMessage = messages[0]?.isWelcome;
  
  // Calculate adjusted index
  const adjustedIndex = hasWelcomeMessage ? messageIndex - 1 : messageIndex;

  // Update local state
  setMessages(prev => prev.map((msg, idx) => 
    idx === messageIndex ? {...msg, liked: newLikedStatus} : msg
  ));

  try {
    await axios.put(`${API_SERVER_URL}/${selectedChatId}/message/${adjustedIndex}/like`, {
      liked: newLikedStatus
    });
  } catch (error) {
    console.error("Error updating like status:", error);
    // Revert on error
    setMessages(prev => prev.map((msg, idx) =>
      idx === messageIndex ? {...msg, liked: !newLikedStatus} : msg
    ));
  }
};


    return (
<div className="chat-container">
      <div className={`sidebar ${showSidebar ? 'active' : ''}`}>
        <div className="sidebar-nav">
          <button
            className="add-chat-button"
            onClick={() => setIsModalOpen(true)}
            disabled={isLoading}
          >
             <IoChatbubblesOutline size={20} color="black" /> Chat
          </button>
           {/* Add Logout Button Here */}
      {isAuthenticated && (
        <div className="sidebar-footer">
          <button 
            className="navbar-button logout" 
            onClick={handleLogout}
          >
             Logout 
          </button>
        </div>
      )}
        </div>
        <div className="chat-list">
          {chats.map((chat) => (
            <div
              key={chat.chatId}
              className={`chat-item ${chat.chatId === selectedChatId ? 'active' : ''}`}
              onClick={() => handleSelectChat(chat.chatId)}
            >
              <div className="chat-item-header">
                <ChatModeIcon mode={chat.mode} />
                <div className="chat-name">{chat.name}</div>
              </div>
              <div className="chat-date">{formatDateTime(chat.createdAt)}</div>
            </div>
          ))}
        </div>
      </div>

      {showSidebar && (
        <div className="sidebar-overlay" onClick={() => setShowSidebar(false)} />
      )}

      {isModalOpen && (
        <ChatModeSelectionModal onSelect={handleChatModeSelect} onClose={handleModalClose} />
      )}

      {isPopupOpen && (
        <Popup
          onClose={() => {
            setIsPopupOpen(false);
            handlePopupClose();
          }}
          onSubmit={(formData) => handlePopupSubmit(formData)}
        />
      )}

      <div className="chat-area">
        <div className="messages" ref={messageRef}>
        {isFetchingHistory ? (
    <div className="loading-indicator">
      {/* <img src={loadingGif} alt="Loading chat history..." /> */}
    </div>
  ) : (
    messages.map((msg, index) => (
      <Message 
        key={`${msg.createdAt}-${index}`} 
        sender={msg.role} 
        text={msg.content}
        messageIndex={index}
        timestamp={msg.createdAt}
        chatId={selectedChatId}
        liked={msg.liked || false}  // Pass liked property from your message
        onToggleLike={updateMessageLike}
      />
    ))
  )}
  {!isFetchingHistory && loadingChatId === selectedChatId && (
    <div className="message loading">
      <img src={loadingGif} alt="Generating response..." />
    </div>
  )}
          <div ref={messagesEndRef} />
        </div>

        <form className="input-area" onSubmit={handleChatboxSubmit}>
          <div className="input-wrapper-chat">
            <textarea
              value={input}
              onKeyDown={handleKeyPress}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message LovingIs..."
              rows="1"
              className="dynamic-textarea"
            />
            <button className="enter-button" type="submit" disabled={!input.trim() || isLoading}>
              <img src={sendIcon} />
            </button>
          </div>

        </form>
      </div>
</div>
    );
};

export default ChatBox;